/**
 * Gesture helpers over Pointer Events (ui-ux §7.1–§7.4): long-press with the gold progress ring, hold-to-activate
 * («К развязке» 400 ms, «Пас» 450 ms) and tap / long-press / drag arbitration. Thresholds come from
 * `input/constants.ts` only. Owner: UI/Visual (F3).
 */
import { haptic } from '../feedback/haptics';
import { DRAG_START_PX, HOLD_SKIP_MS, LONG_PRESS_MS, LONG_PRESS_RING_MS, TAP_SLOP_MOUSE, TAP_SLOP_TOUCH } from './constants';

export type GestureKind = 'tap' | 'longpress' | 'drag';

/** Pure arbitration (§7.4): movement > 10 px → drag; else ≥ 400 ms → long-press; else up → tap. */
export function classifyGesture(moved: number, heldMs: number, pointerType: string): GestureKind {
  const slop = pointerType === 'mouse' ? TAP_SLOP_MOUSE : Math.max(TAP_SLOP_TOUCH, DRAG_START_PX);
  if (moved > slop) return 'drag';
  if (heldMs >= LONG_PRESS_MS) return 'longpress';
  return 'tap';
}

/**
 * Hold-to-activate: a filling gold ring (`--hold` 0..1 on the element, `.is-holding`) completes after `ms`; releasing
 * early cancels and calls `onShortTap` (show the «Удерживай» hint, haptic `deny`). Keyboard Enter/Space fires at once.
 * Returns a disposer.
 */
export function holdToActivate(el: HTMLElement, onFire: () => void, opts: { ms?: number; onShortTap?: () => void } = {}): () => void {
  const ms = opts.ms ?? HOLD_SKIP_MS;
  let raf = 0, t0 = 0, active = false, pointerId = -1;
  const stop = (): void => { active = false; cancelAnimationFrame(raf); el.classList.remove('is-holding'); el.style.setProperty('--hold', '0'); };
  const tick = (now: number): void => {
    if (!active) return;
    const p = Math.min(1, (now - t0) / ms);
    el.style.setProperty('--hold', p.toFixed(3));
    if (p >= 1) { stop(); haptic('confirm'); onFire(); return; }
    raf = requestAnimationFrame(tick);
  };
  const down = (e: PointerEvent): void => {
    if (e.button > 0) return;
    active = true; pointerId = e.pointerId; t0 = performance.now();
    try { el.setPointerCapture(e.pointerId); } catch { /* synthetic */ }
    el.classList.add('is-holding');
    raf = requestAnimationFrame(tick);
  };
  const up = (e: PointerEvent): void => {
    if (!active || e.pointerId !== pointerId) return;
    const held = performance.now() - t0;
    stop();
    if (held < ms) { haptic('deny'); opts.onShortTap?.(); }
  };
  const key = (e: KeyboardEvent): void => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onFire(); } };
  const click = (e: MouseEvent): void => { e.preventDefault(); };
  el.style.touchAction = 'none';
  el.addEventListener('pointerdown', down);
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', stop);
  el.addEventListener('keydown', key);
  el.addEventListener('click', click);
  return () => {
    stop();
    el.removeEventListener('pointerdown', down); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', stop);
    el.removeEventListener('keydown', key); el.removeEventListener('click', click);
  };
}

/**
 * Long-press with the gold ring (R7): the ring appears at 150 ms and fills by 400 ms (`--press` 0..1, `.is-longpress`);
 * fires `onLongPress` (haptic `longpress`) unless the pointer moved past the slop. Returns a disposer.
 */
export function onLongPress(el: HTMLElement, onLongPress: (e: PointerEvent) => void): () => void {
  let timer = 0, raf = 0, sx = 0, sy = 0, t0 = 0, fired = false;
  const clear = (): void => { clearTimeout(timer); cancelAnimationFrame(raf); el.classList.remove('is-longpress'); el.style.removeProperty('--press'); };
  const tick = (now: number): void => {
    const p = (now - t0 - LONG_PRESS_RING_MS) / (LONG_PRESS_MS - LONG_PRESS_RING_MS);
    if (p > 0) { el.classList.add('is-longpress'); el.style.setProperty('--press', Math.min(1, p).toFixed(3)); }
    if (p < 1) raf = requestAnimationFrame(tick);
  };
  const down = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    sx = e.clientX; sy = e.clientY; t0 = performance.now(); fired = false;
    raf = requestAnimationFrame(tick);
    timer = window.setTimeout(() => { fired = true; clear(); haptic('longpress'); onLongPress(e); }, LONG_PRESS_MS);
  };
  const move = (e: PointerEvent): void => { if (Math.hypot(e.clientX - sx, e.clientY - sy) > TAP_SLOP_TOUCH) clear(); };
  const click = (e: MouseEvent): void => { if (fired) { e.preventDefault(); e.stopImmediatePropagation(); fired = false; } };
  el.addEventListener('pointerdown', down);
  el.addEventListener('pointermove', move);
  el.addEventListener('pointerup', clear);
  el.addEventListener('pointercancel', clear);
  el.addEventListener('click', click, true);
  return () => {
    clear();
    el.removeEventListener('pointerdown', down); el.removeEventListener('pointermove', move);
    el.removeEventListener('pointerup', clear); el.removeEventListener('pointercancel', clear); el.removeEventListener('click', click, true);
  };
}
